import { AssetImage } from "@/components/optilase/asset-image";
import { CheckCircle } from "@/components/optilase/icons";

import { TextLink } from "@/components/optilase/text-link";
import { CtaButton } from "@/components/optilase/cta-button";
import { type ButtonVariant } from "@/components/ui/button";
import { accentOn, headingOn, onSurface, type Surface } from "@/components/optilase/surface";
import { sectionSpacing } from "@/components/optilase/section-spacing";
import { cn } from "@/lib/utils";
import { headingLines } from "@/lib/heading-lines";

export type MediaSplitViewProps = {
  heading: string;
  body?: string;
  bodyNodes?: React.ReactNode[];
  image: string;
  imageAlt?: string;
  imageFocus?: string;
  imageBadge?: string;
  imageBadgeSrc?: string;
  imageBadgeAlt?: string;
  ctaLabel?: string;
  ctaHref?: string;
  ctaVariant?: ButtonVariant;
  ctaOutline?: boolean;
  ctaAsLink?: boolean;
  ctaAction?: string | null;
  imageHeightClass?: string;
  imageFill?: boolean;
  imageContain?: boolean;
  splitFrom?: "md" | "lg";
  surface?: Surface | null;
  listItems?: string[];
  listInCard?: boolean;
  cardScope?: "list" | "row";
  listMarker?: "tick" | "dot";
  listTick?: "teal" | "navy";
  reversed?: boolean;
  listHeading?: string;
  listColumns?: 1 | 2;
  listPosition?: "side" | "below";
  footnote?: string;
  footnotePosition?: "inside" | "outside";
  headingSize?: "section" | "sub";
  spacing?: string;
  bodySize?: "small" | "medium" | "large";
  headingColor?: string;
};

export default function MediaSplitView({
  heading,
  body,
  bodyNodes,
  image,
  imageAlt = "",
  imageFocus,
  imageBadge,
  imageBadgeSrc,
  imageBadgeAlt,
  ctaLabel,
  ctaHref = "#",
  ctaVariant = "teal",
  ctaOutline = false,
  ctaAsLink = false,
  ctaAction,
  imageHeightClass = "h-65 md:h-[336px] lg:h-[400px]",
  imageFill = false,
  imageContain = false,
  splitFrom = "lg",
  surface,
  listItems,
  listInCard = false,
  cardScope = "list",
  listMarker = "tick",
  listTick,
  reversed = false,
  listHeading,
  listColumns = 1,
  listPosition = "side",
  footnote,
  footnotePosition = "inside",
  headingSize = "section",
  bodySize = "medium",
  headingColor,
  spacing,
}: MediaSplitViewProps) {
  const hasList = (listItems?.length ?? 0) > 0;
  // The image shares the row, so the text keeps its width and the photo flexes.
  const hasImageCol = Boolean(image) || !hasList;
  const showList = hasList && listPosition === "side";
  // A photo row: the media column holds a picture rather than the list.
  const hasImage = !showList;
  const split = splitFrom === "md";

  const Heading = headingSize === "sub" ? "h3" : "h2";
  const title = heading ? (
    <Heading
      className={cn(
        headingSize === "sub" ? "text-h4 font-semibold" : "text-section",
        showList ? "lg:max-w-[500px]" : "lg:max-w-[525px]",
        headingColor ?? headingOn(surface, "text-teal-07"),
      )}
    >
      {headingLines(heading)}
    </Heading>
  ) : null;

  const paragraphs: React.ReactNode[] =
    bodyNodes && bodyNodes.length > 0
      ? bodyNodes
      : (body ?? "")
          .split(/\n{2,}/)
          .map((p) => p.trim())
          .filter(Boolean);
  const copy = paragraphs.map((p, i) => (
    <p
      key={i}
      className={cn(
        "text-base leading-relaxed tracking-[-0.01em]",
        hasImage && "lg:max-w-[491px]",
        bodySize === "medium" && "lg:text-lg",
        bodySize === "large" && "lg:text-xl",
        onSurface(surface, "text-white/85", "text-grey-07", "text-grey-07"),
      )}
    >
      {p}
    </p>
  ));

  const textCol = reversed ? "lg:col-start-2" : "lg:col-start-1";
  const listCol = reversed ? "lg:col-start-1" : "lg:col-start-2";
  const mdTextCol = reversed ? "md:col-start-2" : "md:col-start-1";
  const mdListCol = reversed ? "md:col-start-1" : "md:col-start-2";

  const ctaPlacement = showList
    ? cn(split && cn(mdTextCol, "md:row-start-2"), textCol, "lg:row-start-2")
    : "mt-3";

  const cta = !ctaLabel ? null : ctaAsLink ? (
    <TextLink href={ctaHref} className={cn(ctaPlacement, accentOn(surface, "text-teal-07"))}>
      {ctaLabel}
    </TextLink>
  ) : (
    <CtaButton
      href={ctaHref}
      action={ctaAction}
      variant={ctaVariant}
      className={cn(
        ctaPlacement,
        showList ? "w-full md:w-fit" : "w-fit",
        ctaOutline && "hover:bg-teal-01 border bg-transparent",
        ctaOutline &&
          (surface
            ? cn(accentOn(surface, "text-teal-07"), "border-current")
            : "border-teal-07 text-teal-07"),
      )}
    >
      {ctaLabel}
    </CtaButton>
  );

  const onTint =
    !!surface && !surface.onDark && surface.backgroundColor.toLowerCase() !== "#ffffff";

  const headingLine = listHeading ? (
    <p
      className={cn(
        "tracking-[-0.01em]",
        cardScope === "row"
          ? "text-grey-08 text-lg leading-normal font-medium lg:text-xl"
          : cn(
              "text-h5 font-semibold",
              listInCard
                ? onTint
                  ? "text-teal-07"
                  : "text-teal-08"
                : accentOn(surface, "text-teal-07"),
            ),
      )}
    >
      {listHeading}
    </p>
  ) : null;

  const bullets = (
    <ul
      className={cn(
        "flex flex-col",
        listMarker === "dot" ? "gap-0" : "gap-1",
        listColumns === 2 && "md:block md:columns-2 md:gap-x-10",
      )}
    >
      {listItems?.map((item) => (
        <li
          key={item}
          className={cn(
            "flex break-inside-avoid items-start gap-4",
            listColumns === 2 && listMarker !== "dot" && "md:mb-1",
          )}
        >
          {/* Height is the text's own line box, so the marker stays centred on
              the first line at every body size. */}
          <span
            aria-hidden
            className={cn(
              "flex shrink-0 items-center",
              "text-base",
              bodySize === "medium" && "lg:text-lg",
              bodySize === "large" && "lg:text-xl",
              listMarker === "dot" ? "h-[2.2em]" : "mt-[3.5px] h-[1.625em]",
            )}
          >
            {listMarker === "dot" ? (
              <span
                className={cn(
                  "size-1.5 rounded-full",
                  onSurface(surface, "bg-white/70", "bg-grey-07", "bg-grey-07"),
                )}
              />
            ) : (
              <CheckCircle
                className={cn(
                  "size-6",
                  listTick === "navy"
                    ? listInCard
                      ? "text-navy-03"
                      : onSurface(surface, "text-white", "text-navy-03", "text-navy-03")
                    : listInCard
                      ? "text-teal-06"
                      : accentOn(surface, "text-teal-07"),
                )}
              />
            )}
          </span>
          <span
            className={cn(
              "text-base tracking-[-0.01em]",
              listMarker === "dot" ? "leading-[2.2]" : "py-[3.5px] leading-relaxed",
              bodySize === "medium" && "lg:text-lg",
              bodySize === "large" && "lg:text-xl",
              listInCard
                ? "text-grey-07"
                : onSurface(surface, "text-white/85", "text-grey-07", "text-grey-07"),
            )}
          >
            {item}
          </span>
        </li>
      ))}
    </ul>
  );

  const sectionClass = cn(
    "px-5 md:px-10 lg:px-20",
    sectionSpacing(spacing),
    surface ? "" : "bg-white",
  );
  const sectionStyle = surface ? { backgroundColor: surface.backgroundColor } : undefined;

  const card = listInCard ? `rounded-2xl p-6 lg:p-8 ${onTint ? "bg-white" : "bg-teal-01"}` : "";

  // Only a card can hold it; otherwise it falls under the section either way.
  const footnoteInCard = footnotePosition === "inside" && hasList && listPosition === "below";
  const footnoteOutside = !footnoteInCard;

  const belowList =
    hasList && listPosition === "below" ? (
      <div className="flex flex-col gap-5">
        {listInCard ? null : headingLine}
        <div
          className={cn(
            "flex flex-col gap-5",
            listInCard
              ? card
              : "rounded-2xl border p-6 lg:p-8" +
                  (surface ? " border-current/15" : " border-grey-01"),
          )}
        >
          {listInCard ? headingLine : null}
          {bullets}
          {footnoteInCard && footnote ? (
            <p className="text-grey-07 text-base leading-normal tracking-[-0.01em] lg:text-lg">
              {footnote}
            </p>
          ) : null}
        </div>
      </div>
    ) : null;

  const footnoteLine =
    footnote && footnoteOutside ? (
      <p
        className={cn(
          "text-base leading-relaxed tracking-[-0.01em]",
          onSurface(surface, "text-white/85", "text-grey-07", "text-grey-07"),
        )}
      >
        {footnote}
      </p>
    ) : null;

  const row = showList ? (
    <div
      className={cn(
        "grid gap-6",
        // The whole row inside one card, rather than the list alone (1:7515).
        cardScope === "row" && card,
        split && "md:grid-cols-[minmax(0,1fr)_minmax(0,400px)] md:gap-x-8",
        "lg:grid-cols-[minmax(0,560px)_minmax(0,640px)] lg:justify-between lg:gap-x-16",
      )}
    >
      <div
        className={cn(
          "flex flex-col gap-5",
          split && cn(mdTextCol, "md:row-start-1"),
          textCol,
          "lg:row-start-1",
        )}
      >
        {title}
        {copy}
      </div>

      <div
        className={cn(
          "flex flex-col gap-5",
          split && cn(mdListCol, "md:row-span-2 md:row-start-1 md:self-center"),
          listCol,
          "lg:row-span-2 lg:row-start-1 lg:self-center",
          cardScope === "row" ? "" : card,
        )}
      >
        {headingLine}
        {bullets}
      </div>

      {cta}
    </div>
  ) : (
    <div
      className={cn(
        "flex flex-col gap-6 lg:flex-row lg:justify-between lg:gap-16",
        imageFill && !imageContain ? "lg:items-stretch" : "lg:items-center",
        split && "md:flex-row md:justify-between md:gap-8",
        split && (imageFill && !imageContain ? "md:items-stretch" : "md:items-center"),
      )}
    >
      <div
        className={cn(
          "order-1 flex flex-col gap-5 lg:max-w-140",
          hasImageCol && "lg:w-140 lg:shrink-0",
          imageFill && !imageContain && "lg:justify-center",
          reversed ? "lg:order-2" : "lg:order-1",
          split && "md:max-w-64.75 lg:max-w-140",
          split && (reversed ? "md:order-2" : "md:order-1"),
        )}
      >
        {title}
        {copy}
        {cta}
      </div>

      {image || !hasList ? (
        <div
          className={cn(
            "relative order-2 w-full max-lg:shrink-0 lg:max-w-160 lg:flex-1",
            imageBadge || imageBadgeSrc || imageContain
              ? ""
              : "overflow-hidden rounded-2xl lg:rounded-[20px]",
            reversed ? "lg:order-1" : "lg:order-2",
            split && (reversed ? "md:order-1" : "md:order-2"),
            split && "md:w-100",
            imageFill && !imageContain
              ? "h-65 md:h-[336px] lg:my-10 lg:h-auto lg:min-h-100"
              : imageHeightClass,
          )}
        >
          <AssetImage
            src={image}
            focus={imageFocus}
            alt={imageAlt}
            fill
            sizes="(min-width: 1115px) 640px, 100vw"
            className={cn(
              imageContain ? "object-contain" : "object-cover",
              !imageContain && (imageBadge || imageBadgeSrc) && "rounded-2xl lg:rounded-[20px]",
            )}
          />
          {imageBadgeSrc ? (
            <span className="absolute top-6 -left-3 z-10 flex size-20 items-center justify-center overflow-hidden rounded-full bg-white p-1.5 shadow-[0_8px_24px_-6px_rgba(39,6,88,0.25)] lg:top-12 lg:-left-7.5 lg:size-30 lg:p-2">
              <AssetImage
                src={imageBadgeSrc}
                alt={imageBadgeAlt || ""}
                width={120}
                height={120}
                className="size-full object-contain"
              />
            </span>
          ) : null}
          {imageBadge ? (
            <p className="bg-teal-07 absolute top-6 -left-3 z-10 flex max-w-[60%] items-center justify-center rounded-lg px-3 py-2.5 text-center text-sm leading-tight font-bold text-white lg:top-12 lg:-left-7.5 lg:max-w-[220px] lg:px-4 lg:py-3 lg:text-base">
              {imageBadge}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  return (
    <section style={sectionStyle} className={sectionClass}>
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 lg:gap-10">
        {heading || body || image || showList ? row : null}
        {belowList}
        {footnoteLine}
      </div>
    </section>
  );
}
